/** Vendors / Accounts - what each group of meters is billed under (ADR-0062, ADR-0212).
 *
 * One row per supply account: the vendor, the reference **as typed** (never matched against a bill,
 * SEC-05), how many meters sit on it, its life and whether it has ended. Ending an account stops every
 * stream beneath it being expected from that date; that act, attaching meters and moving them are in
 * the drawer a row opens (`AccountRow`, unchanged), so nothing the register could do is lost.
 */

import { Receipt } from "@phosphor-icons/react";
import { useState } from "react";

import { useMe } from "../../api/auth";
import { useCoverage, useRegisters } from "../../api/sources";
import type { SupplyAccount } from "../../api/sources";
import { ReadFailed } from "../../components/Loaded";
import { NotYourRole } from "../../components/NotYourRole";
import { AddImportActions } from "../../components/ui/AddImportActions";
import { Drawer } from "../../components/ui/Drawer";
import { EmptyAction } from "../../components/ui/EmptyAction";
import { EmptyState } from "../../components/ui/EmptyState";
import { FilterLine } from "../../components/ui/FilterLine";
import { EMPTY_FILTERS } from "../../components/ui/filters";
import type { FilterDef, FilterState } from "../../components/ui/filters";
import { GridTable } from "../../components/ui/GridTable";
import type { GridColumn } from "../../components/ui/GridTable";
import { InfoPopover } from "../../components/ui/InfoPopover";
import type { SortState } from "../../components/ui/sortRows";
import { StatusChip } from "../../components/ui/StatusChip";
import { TablePage } from "../../components/ui/TablePage";
import { Tile, TileStrip } from "../../components/ui/Tile";
import { AccountRow, lifeOf } from "../SourcesRegisters";
import { keepSearch } from "../search";
import type { Screen } from "../useScreen";
import { count, matches, ordered, usePlaceLabel } from "../tableKit";
import { MAY_RECORD } from "./roles";
import { AddAccountDrawer } from "./VendorForms";

export function AccountsTab({ screen }: { screen: Screen }) {
  const label = usePlaceLabel();
  const role = useMe().data?.role;
  const registers = useRegisters();
  const window = screen.window;
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [sort, setSort] = useState<SortState | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const coverage = useCoverage(window?.period_start, window?.period_end, Boolean(window) && open !== null);

  const mayRecord = role !== undefined && MAY_RECORD.includes(role);
  const data = registers.data;
  const defs: FilterDef[] = [
    {
      id: "vendor",
      label: "Vendor",
      mode: "single",
      options: (data?.vendors ?? []).map((vendor) => ({ value: vendor.id, label: vendor.name })),
    },
    {
      id: "status",
      label: "Status",
      mode: "single",
      options: [
        { value: "active", label: "Active" },
        { value: "retired", label: "Retired" },
      ],
    },
  ];
  const vendor = filters.values.vendor?.[0];
  const status = filters.values.status?.[0];
  const shown = ordered(
    (data?.accounts ?? []).filter(
      (a) =>
        (!vendor || a.vendor_id === vendor) &&
        (!status || (status === "retired") === a.is_retired) &&
        matches(filters.search, a.vendor_name, a.account_ref),
    ),
    sort,
    {
      vendor: (a) => a.vendor_name,
      reference: (a) => a.account_ref,
      meters: (a) => a.meters.length,
    },
  );
  const opened = data?.accounts.find((account) => account.id === open);

  const columns: GridColumn<SupplyAccount>[] = [
    { id: "vendor", header: "Vendor", sortable: true, cell: (a) => a.vendor_name },
    { id: "reference", header: "Reference", sortable: true, cell: (a) => a.account_ref },
    { id: "meters", header: "Meters", numeric: true, sortable: true, cell: (a) => count(a.meters.length) },
    { id: "life", header: "Life", cell: (a) => lifeOf(a) },
    {
      id: "status",
      header: "Status",
      cell: (a) =>
        a.is_retired ? <StatusChip tone="neutral">Retired</StatusChip> : <StatusChip tone="ok">Active</StatusChip>,
    },
  ];

  return (
    <>
      <TablePage
        icon={Receipt}
        title="Vendors"
        label={label}
        actions={
          <>
            <InfoPopover infoKey="vendors.accounts" />
            <AddImportActions
              role={mayRecord ? role : undefined}
              addLabel="Add account"
              onAdd={() => setAdding(true)}
            />
          </>
        }
        extra={
          role !== undefined && !mayRecord ? <NotYourRole action="addAccount" testId="accounts-who-can" /> : undefined
        }
        tiles={
          data ? (
            <TileStrip label="Accounts summary">
              <Tile value={count(data.accounts.length)} label="Accounts" />
              <Tile value={count(data.accounts.filter((a) => a.is_retired).length)} label="Retired accounts" />
              <Tile
                value={count(data.not_recorded.length)}
                label="Meters without an account"
                infoKey="vendors.not-recorded"
              />
            </TileStrip>
          ) : undefined
        }
        filters={
          data && data.accounts.length === 0 ? undefined : (
            <FilterLine
              filters={defs}
              state={filters}
              onChange={setFilters}
              searchLabel="Search accounts"
              resultCount={data ? { shown: shown.length, total: data.accounts.length, noun: "accounts" } : undefined}
            />
          )
        }
        table={
          data ? (
            <GridTable
              caption="Accounts"
              columns={columns}
              rows={shown}
              rowKey={(a) => a.id}
              rowLabel={(a) => `${a.vendor_name} ${a.account_ref}`}
              sort={sort}
              onSortChange={setSort}
              onRowOpen={(a) => setOpen(a.id)}
              empty={
                data.accounts.length === 0 ? (
                  <EmptyState
                    icon={Receipt}
                    title="No accounts yet"
                    compact
                    testId="accounts-empty"
                    action={
                      data.vendors.length === 0 ? (
                        <EmptyAction
                          label="Add a vendor first"
                          allowed={role === undefined ? undefined : mayRecord}
                          to="/vendors"
                          search={keepSearch}
                          testId="accounts-empty-vendor"
                        />
                      ) : (
                        <EmptyAction
                          label="Add account"
                          allowed={role === undefined ? undefined : mayRecord}
                          onClick={() => setAdding(true)}
                          testId="accounts-empty-add"
                        />
                      )
                    }
                  >
                    An account is a vendor and a reference.
                  </EmptyState>
                ) : undefined
              }
              testId="accounts-table"
            />
          ) : undefined
        }
        state={
          registers.error ? (
            <ReadFailed
              of="The accounts"
              error={registers.error}
              onRetry={() => void registers.refetch()}
            />
          ) : !data ? (
            <GridTable
              caption="Accounts"
              columns={[]}
              rows={[]}
              rowKey={(a: SupplyAccount) => a.id}
              loading
              testId="accounts-loading"
            />
          ) : undefined
        }
      />
      <Drawer
        open={opened !== undefined}
        onClose={() => setOpen(null)}
        title={opened ? `${opened.vendor_name} ${opened.account_ref}` : "Account"}
        width="lg"
      >
        {opened && coverage.error ? (
          <ReadFailed
            of="The expected bills"
            error={coverage.error}
            onRetry={() => void coverage.refetch()}
          />
        ) : null}
        {opened && data ? (
          <AccountRow
            account={opened}
            available={[
              ...data.not_recorded,
              ...data.accounts
                .filter((other) => other.id !== opened.id)
                .flatMap((other) => other.meters.filter((m) => !m.operational_to)),
            ]}
            mayDeclare={mayRecord}
            streams={coverage.data?.sources ?? []}
          />
        ) : null}
      </Drawer>
      <AddAccountDrawer
        open={adding}
        onClose={() => setAdding(false)}
        vendors={data?.vendors ?? []}
        vendorId={vendor}
      />
    </>
  );
}
